import { TDocumentData } from "../models";
import {
  deleteDocumentDataById,
  uploadRawDocumentFile,
} from "../network";
import {
  FILES_NAMESPACE,
  TFileIntentMap,
  buildKeepIntent,
  normalizeFileIntents,
} from "./fileIntents";

export const IDENTIFICATION_DOCUMENT_FILE_FIELD_NAME = "document";

type IdentificationFileOps = {
  deletes: { id: string }[];
  uploads: { dataId: string; file: File }[];
};

type IdentificationFileOptions = {
  createDataId?: (fieldName: string) => string;
};

function fallbackUuid(): string {
  return `${Date.now().toString(36)}-${Math.random()
    .toString(16)
    .slice(2)}`;
}

export function createRawIdentificationFileDataId(
  fieldName = IDENTIFICATION_DOCUMENT_FILE_FIELD_NAME,
): string {
  const cryptoApi = (globalThis as {
    crypto?: { randomUUID?: () => string };
  }).crypto;
  const id =
    cryptoApi && typeof cryptoApi.randomUUID === "function"
      ? cryptoApi.randomUUID()
      : fallbackUuid();

  return `${fieldName}:${id}`;
}

export function buildInitialIdentificationFileIntents(
  fileItems: TDocumentData[],
): { [FILES_NAMESPACE]?: TFileIntentMap } {
  const files = fileItems.filter((item) => item.type === "file");
  if (files.length === 0) {
    return {};
  }

  return {
    [FILES_NAMESPACE]: {
      [IDENTIFICATION_DOCUMENT_FILE_FIELD_NAME]: files.map(buildKeepIntent),
    },
  };
}

export function planIdentificationFileOperations(
  files: TFileIntentMap | undefined,
  options: IdentificationFileOptions = {},
): IdentificationFileOps {
  const ops: IdentificationFileOps = { deletes: [], uploads: [] };
  if (!files) return ops;

  const createDataId =
    options.createDataId ?? createRawIdentificationFileDataId;

  for (const fieldName in files) {
    const intents = normalizeFileIntents(files[fieldName]);

    for (const intent of intents) {
      if (intent.mode === "delete") {
        ops.deletes.push({ id: intent.id });
      } else if (intent.mode === "upload") {
        ops.uploads.push({
          dataId: createDataId(fieldName),
          file: intent.file,
        });
      }
    }
  }

  return ops;
}

export async function processIdentificationFileIntents(
  documentId: string,
  files: TFileIntentMap | undefined,
  options: IdentificationFileOptions = {},
): Promise<void> {
  const { deletes, uploads } = planIdentificationFileOperations(files, options);

  if (deletes.length > 0) {
    await Promise.all(deletes.map(({ id }) => deleteDocumentDataById(id)));
  }

  if (uploads.length > 0) {
    await Promise.all(
      uploads.map(({ dataId, file }) =>
        uploadRawDocumentFile(documentId, dataId, file),
      ),
    );
  }
}
